import {useCallback, useEffect, useRef, useState} from 'react'
import {DayPicker} from 'react-day-picker'

interface DateTimePickerProps {
  value: Date | null
  onChange: (date: Date) => void
  label?: string
  minDate?: Date | null
  hasError?: boolean
}

const INPUT_STYLE =
  'flex w-full items-center gap-2 rounded-lg border bg-moss-white px-3 py-[11px] text-14 font-normal leading-5 tracking-[.16px] text-moss-black focus:outline-none'

const TIMES: string[] = []

for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIMES.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
  }
}

const ClockIcon = () => (
  <svg
    className="size-4 shrink-0 text-grey-450"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
)

const CalendarIcon = () => (
  <svg
    className="size-4 shrink-0 text-grey-450"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 9v9.75"
    />
  </svg>
)

export const DateTimePicker = ({value, onChange, label, minDate, hasError}: DateTimePickerProps) => {
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [timeOpen, setTimeOpen] = useState(false)
  const calendarRef = useRef<HTMLDivElement>(null)
  const timeRef = useRef<HTMLDivElement>(null)

  const timeValue = value
    ? `${value.getHours().toString().padStart(2, '0')}:${value.getMinutes().toString().padStart(2, '0')}`
    : ''

  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setCalendarOpen(false)
      }

      if (timeRef.current && !timeRef.current.contains(e.target as Node)) {
        setTimeOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDaySelect = useCallback(
    (day: Date | undefined) => {
      if (!day) {
        return
      }

      const updated = new Date(day)
      const hours = value ? value.getHours() : 8
      const minutes = value ? value.getMinutes() : 0
      updated.setHours(hours, minutes)
      onChange(updated)
      setCalendarOpen(false)
    },
    [value, onChange],
  )

  const handleTimeSelect = useCallback(
    (timeString: string) => {
      const [h, m] = timeString.split(':').map(Number)

      if (value) {
        const updated = new Date(value)
        updated.setHours(h, m)
        onChange(updated)
      } else {
        const today = new Date()
        today.setHours(h, m, 0, 0)
        onChange(today)
      }

      setTimeOpen(false)
    },
    [value, onChange],
  )

  const formatDate = (date: Date | null) => {
    if (!date) {
      return null
    }

    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  return (
    <div>
      {label && <label className="mb-1 block text-12 font-medium text-grey-550">{label}</label>}
      <div className="grid grid-cols-[1fr_110px] gap-2">
        <div ref={calendarRef} className="relative">
          <button
            type="button"
            onClick={() => {
              setCalendarOpen(!calendarOpen)
              setTimeOpen(false)
            }}
            className={`${INPUT_STYLE} ${
              hasError
                ? 'border-ui-red-300 hover:border-ui-red-300 focus:border-ui-red-300'
                : 'border-grey-150 hover:bg-grey-110'
            }`}
          >
            <CalendarIcon />
            <span className={formatDate(value) ? '' : 'text-grey-450'}>{formatDate(value) ?? 'Date'}</span>
          </button>

          {calendarOpen && (
            <div className="absolute left-0 top-full z-40 mt-1 w-fit rounded-lg border border-grey-150 bg-moss-white shadow-elevation-3-floating">
              <div className="p-1 min-[350px]:p-4">
                <DayPicker
                  mode="single"
                  selected={value ?? undefined}
                  onSelect={handleDaySelect}
                  disabled={minDate ? {before: minDate} : undefined}
                  classNames={{
                    root: 'text-14',
                    months: 'relative flex flex-col',
                    month_caption: 'flex h-8 items-center justify-center font-medium text-moss-black',
                    nav: 'absolute inset-x-0 top-0 flex h-8 items-center justify-between px-1',
                    button_previous:
                      'inline-flex size-8 items-center justify-center rounded-lg text-grey-450 hover:bg-grey-100 hover:text-moss-black',
                    button_next:
                      'inline-flex size-8 items-center justify-center rounded-lg text-grey-450 hover:bg-grey-100 hover:text-moss-black',
                    weekdays: 'flex',
                    weekday: 'w-9 py-1.5 text-center text-12 font-medium text-grey-450',
                    week: 'flex',
                    day: 'relative size-9 p-0 text-center text-13',
                    day_button:
                      'inline-flex size-9 items-center justify-center rounded-lg transition-colors hover:bg-grey-100',
                    selected: '!bg-green-dark !text-moss-white rounded-lg',
                    today: 'font-bold text-moss-green-500',
                    outside: 'text-grey-300',
                    disabled: 'text-grey-300 opacity-50',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div ref={timeRef} className="relative">
          <button
            type="button"
            onClick={() => {
              setTimeOpen(!timeOpen)
              setCalendarOpen(false)
            }}
            className={`${INPUT_STYLE} border-grey-150 hover:bg-grey-110`}
          >
            <ClockIcon />
            <span className={timeValue ? '' : 'text-grey-450'}>{timeValue || 'Time'}</span>
          </button>

          {timeOpen && (
            <div className="absolute right-0 top-full z-40 mt-1 max-h-[240px] w-[110px] overflow-y-auto rounded-lg border border-grey-150 bg-moss-white py-1 shadow-elevation-3-floating">
              {TIMES.map((time) => (
                <button
                  key={time}
                  type="button"
                  onClick={() => handleTimeSelect(time)}
                  className={`flex w-full items-center px-3 py-[6px] text-13 transition-colors ${
                    time === timeValue
                      ? 'bg-green-dark font-medium text-moss-white'
                      : 'text-moss-black hover:bg-grey-100'
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
